import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/auth';
import { FileText, Plus, Search, Filter, SortAsc, SortDesc, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuCheckboxItem } from '@/components/ui/dropdown-menu';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import TemplatesTable from '../../../templates/TemplatesTable';
import CreateTemplateDialog from '../../../templates/CreateTemplateDialog';
import { useSmsTemplatesManager } from '@/hooks/query/useSmsTemplates';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SmsTemplate } from '@/hooks/sms/types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useRestaurantContext } from '@/context/RestaurantContext';
import { UserRole } from '@/context/auth/types';
import { Skeleton } from '@/components/ui/skeleton';

const ITEMS_PER_PAGE = 10;

const TemplatesTab: React.FC = () => {
  const { user } = useAuth();
  const { selectedRestaurants, activeRestaurantDetails, restaurantDetails } = useRestaurantContext();
  const userRestaurants = restaurantDetails || [];
  
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [view, setView] = useState<'all' | 'org' | 'restaurant'>('all');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [sortField, setSortField] = useState<'name' | 'date' | 'usage'>('date');
  const [templateToEdit, setTemplateToEdit] = useState<SmsTemplate | null>(null);
  const [templateToDuplicate, setTemplateToDuplicate] = useState<SmsTemplate | null>(null);
  const [templateToDelete, setTemplateToDelete] = useState<SmsTemplate | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  const { 
    templates, 
    isLoading, 
    isError, 
    error,
    refreshTemplates,
    createTemplate,
    deleteTemplate,
    isDeleting
  } = useSmsTemplatesManager(user, selectedRestaurants);
  
  const canCreateTemplate = user?.role === UserRole.SUPER_ADMIN || 
                           user?.role === UserRole.ORG_ADMIN || 
                           user?.role === UserRole.REST_ADMIN;
  
  const hasTemplates = templates && templates.length > 0;
  
  const categories = [...new Set(templates.map(template => template.category || 'General'))];
  
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = !searchQuery || 
      (template.name && template.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (template.content && template.content.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = categoryFilter.length === 0 || 
      (template.category && categoryFilter.includes(template.category));
    
    let matchesView = true;
    if (view === 'org') {
      matchesView = !template.restaurant_id || template.restaurant_id === 'org';
    } else if (view === 'restaurant') {
      matchesView = !!template.restaurant_id && template.restaurant_id !== 'org';
    }
    
    return matchesSearch && matchesCategory && matchesView;
  }).sort((a, b) => {
    if (sortField === 'name') {
      return sortOrder === 'asc' 
        ? (a.name || '').localeCompare(b.name || '') 
        : (b.name || '').localeCompare(a.name || '');
    } else if (sortField === 'usage') {
      const usageA = a.usageCount || 0;
      const usageB = b.usageCount || 0;
      return sortOrder === 'asc' ? usageA - usageB : usageB - usageA;
    } else {
      const dateA = new Date(a.lastUsed || '').getTime() || 0;
      const dateB = new Date(b.lastUsed || '').getTime() || 0;
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    }
  });
  
  const totalPages = Math.max(1, Math.ceil(filteredTemplates.length / ITEMS_PER_PAGE));
  const paginatedTemplates = filteredTemplates.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  
  const handleCategoryToggle = (category: string) => {
    setCategoryFilter(prev => {
      if (prev.includes(category)) {
        return prev.filter(c => c !== category);
      } else {
        return [...prev, category];
      }
    });
  };
  
  const handleEditTemplate = (id: string) => {
    const template = templates.find(t => t.id === id);
    if (template) {
      setTemplateToEdit(template);
      setIsCreateDialogOpen(true);
    }
  };
  
  const handleDuplicateTemplate = (id: string) => {
    const template = templates.find(t => t.id === id);
    if (template) {
      // Create a duplicated template with modified name
      const duplicatedTemplate = {
        ...template,
        name: `Copy of ${template.name}`,
        id: undefined // Remove ID so it creates a new template
      };
      setTemplateToDuplicate(duplicatedTemplate);
      setIsCreateDialogOpen(true);
    }
  };
  
  const handleDeleteTemplate = (id: string) => {
    const template = templates.find(t => t.id === id);
    if (template) {
      setTemplateToDelete(template);
      setIsDeleteDialogOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete || !activeRestaurantDetails?.id) return;

    try {
      await deleteTemplate({
        templateId: templateToDelete.id,
        restaurantId: activeRestaurantDetails.id
      });
      setIsDeleteDialogOpen(false);
      setTemplateToDelete(null);
    } catch (error) {
      console.error('Error deleting template:', error);
      // Error handling is managed by the hook with toast notifications
    }
  };
  
  const handleSort = (field: 'name' | 'date' | 'usage') => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };
  
  const showNoRestaurantWarning = selectedRestaurants.length === 0;

  return (
    <div className="py-4">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-medium">Message Templates</h3>
        <Button 
          onClick={() => setIsCreateDialogOpen(true)}
          disabled={!canCreateTemplate || showNoRestaurantWarning}
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          New Template
        </Button>
      </div>
      
      {showNoRestaurantWarning ? (
        <Alert variant="default" className="mb-4 border-amber-500 bg-amber-50 text-amber-900">
          <AlertCircle className="h-4 w-4 text-amber-500" />
          <AlertDescription>
            Please select a restaurant to view and manage templates
          </AlertDescription>
        </Alert>
      ) : isError ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : "Failed to load templates"}
          </AlertDescription>
        </Alert>
      ) : (
        <Card>
          <CardContent className="p-6">
            {isLoading ? (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Skeleton className="h-10 w-64" />
                  <Skeleton className="h-10 w-32" />
                </div>
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              </div>
            ) : !hasTemplates ? (
              <div className="flex flex-col items-center text-center p-8">
                <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Create Your First Template</h3>
                <p className="text-muted-foreground mb-4">
                  Templates help you save time and maintain consistent messaging across campaigns.
                </p>
                <Button 
                  onClick={() => setIsCreateDialogOpen(true)}
                  disabled={!canCreateTemplate}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Create Template
                </Button>
              </div>
            ) : (
              <>
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search templates..."
                      className="pl-8"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Tabs
                      value={view}
                      onValueChange={(v) => setView(v as 'all' | 'org' | 'restaurant')}
                      className="w-auto"
                    >
                      <TabsList className="grid grid-cols-3 w-[300px]">
                        <TabsTrigger value="all">All</TabsTrigger>
                        <TabsTrigger value="org">Organisation</TabsTrigger>
                        <TabsTrigger value="restaurant">Restaurant</TabsTrigger>
                      </TabsList>
                    </Tabs>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-2">
                          <Filter className="h-4 w-4" />
                          {categoryFilter.length > 0 ? (
                            <Badge variant="secondary" className="rounded-sm">
                              {categoryFilter.length}
                            </Badge>
                          ) : (
                            "Filter"
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel>Filter by Category</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {categories.map((category) => (
                          <DropdownMenuCheckboxItem
                            key={category}
                            checked={categoryFilter.includes(category)}
                            onCheckedChange={() => handleCategoryToggle(category)}
                          >
                            {category}
                          </DropdownMenuCheckboxItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-2">
                          {sortOrder === 'asc' ? (
                            <SortAsc className="h-4 w-4" />
                          ) : (
                            <SortDesc className="h-4 w-4" />
                          )}
                          Sort
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Sort Templates</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuCheckboxItem
                          checked={sortField === 'name'}
                          onCheckedChange={() => handleSort('name')}
                        >
                          By Name
                        </DropdownMenuCheckboxItem>
                        <DropdownMenuCheckboxItem
                          checked={sortField === 'date'}
                          onCheckedChange={() => handleSort('date')}
                        >
                          By Date
                        </DropdownMenuCheckboxItem>
                        <DropdownMenuCheckboxItem
                          checked={sortField === 'usage'}
                          onCheckedChange={() => handleSort('usage')}
                        >
                          By Usage
                        </DropdownMenuCheckboxItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                
                <TemplatesTable
                  templates={paginatedTemplates}
                  onEdit={handleEditTemplate}
                  onDuplicate={handleDuplicateTemplate}
                  onDelete={handleDeleteTemplate}
                />
                
                {totalPages > 1 && (
                  <Pagination className="mt-4">
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious 
                          onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                          className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                        />
                      </PaginationItem>
                      
                      {Array.from({ length: totalPages }).map((_, i) => {
                        if (
                          i === 0 || 
                          i === totalPages - 1 || 
                          (i >= currentPage - 2 && i <= currentPage + 0)
                        ) {
                          return (
                            <PaginationItem key={i}>
                              <PaginationLink
                                isActive={currentPage === i + 1}
                                onClick={() => handlePageChange(i + 1)}
                              >
                                {i + 1}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        } else if (
                          i === currentPage - 3 || 
                          i === currentPage + 1
                        ) {
                          return (
                            <PaginationItem key={i}>
                              <PaginationLink>...</PaginationLink>
                            </PaginationItem>
                          );
                        }
                        return null;
                      })}
                      
                      <PaginationItem>
                        <PaginationNext
                          onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                          className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
      
      <CreateTemplateDialog
        isOpen={isCreateDialogOpen}
        onOpenChange={(open) => {
          setIsCreateDialogOpen(open);
          if (!open) {
            setTemplateToEdit(null);
            setTemplateToDuplicate(null);
          }
        }}
        restaurantId={activeRestaurantDetails?.id}
        organisationId={activeRestaurantDetails?.organisationId}
        onSuccess={refreshTemplates}
        restaurants={userRestaurants}
        templateToEdit={templateToEdit || templateToDuplicate}
        isDuplicating={!!templateToDuplicate}
      />
      
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the template "{templateToDelete?.name}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setIsDeleteDialogOpen(false);
              setTemplateToDelete(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete Template'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TemplatesTab;
